'use client';

import { useState, useEffect } from 'react';
import { Lock, Search, CheckCircle2, Send, Bot, Check, Info, Phone, Mic, Plus, FileText, Tags, KanbanSquare } from 'lucide-react';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import styles from './ChatModule.module.css';

export function ChatModule({ reverse = false }: { reverse?: boolean }) {
  // A Máquina de Estados:
  // 0: Mensagem do Cliente
  // 1: Bot digitando
  // 2: Bot responde
  // 3: Cliente pede humano
  // 4: Sistema avisa transferência
  // 5: Humano digitando Nota Oculta
  // 6: Nota Oculta aparece
  // 7: Humano digitando Resposta
  // 8: Resposta enviada + Tag de CRM adicionada
  const [step, setStep] = useState(0);
  const [typedText, setTypedText] = useState("");

  const internalNoteStr = "Lead quente veio do Insta. Mandar valores do plano Pro.";
  const humanReplyStr = "Olá, Loja Fitness! Tudo bem? Vou te mandar a tabela agora.";

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (step === 0) {
      setTypedText('');
      timeout = setTimeout(() => setStep(1), 1500);
    } 
    else if (step === 1) timeout = setTimeout(() => setStep(2), 1500); // Bot typing
    else if (step === 2) timeout = setTimeout(() => setStep(3), 2000); // Wait for client
    else if (step === 3) timeout = setTimeout(() => setStep(4), 1000); // Client said "yes"
    else if (step === 4) timeout = setTimeout(() => setStep(5), 1500); // Transfer alert
    else if (step === 5) { // Humano typing note
      let i = 0;
      const typeInterval = setInterval(() => {
        setTypedText(internalNoteStr.substring(0, i + 1));
        i++;
        if (i === internalNoteStr.length) {
          clearInterval(typeInterval);
          setTimeout(() => { setTypedText(''); setStep(6); }, 500);
        }
      }, 40);
      return () => clearInterval(typeInterval);
    } 
    else if (step === 6) timeout = setTimeout(() => setStep(7), 1000); // Note visible
    else if (step === 7) { // Humano typing reply
      let i = 0;
      const typeInterval = setInterval(() => {
        setTypedText(humanReplyStr.substring(0, i + 1));
        i++;
        if (i === humanReplyStr.length) {
          clearInterval(typeInterval);
          setTimeout(() => { setTypedText(''); setStep(8); }, 500);
        }
      }, 40);
      return () => clearInterval(typeInterval);
    }
    else if (step === 8) {
      timeout = setTimeout(() => setStep(0), 4000); // Fica 4s pronto e reseta
    }

    return () => clearTimeout(timeout);
  }, [step]);

  return (
    <div className={styles.moduleWrapper} style={{ flexDirection: reverse ? 'row-reverse' : 'row' }}>
      
      {/* ==============================================
          COLUNA ESQUERDA: TEXTO E ARGUMENTOS
          ============================================== */}
      <div className={styles.textContent}>
        <span className={styles.badge}>Aesir Chat Omnichannel</span>
        <h3 className={styles.title}>O Palco do seu Time de Vendas</h3>
        <p className={styles.description}>
          Conecte WhatsApp, Instagram, Telegram e Site na mesma tela. Robôs humanizados qualificam e transferem para o atendente com todo o contexto e dossiê do CRM.
        </p>

        <div className={styles.featureList}>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Bot size={16} color="#8b5cf6" /></div>
            Chatbots IA que resolvem dúvidas sozinhos.
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Lock size={16} color="#f59e0b" /></div>
            Notas internas invisíveis para o cliente.
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><KanbanSquare size={16} color="#10b981" /></div>
            Crie orçamentos e atualize funis sem sair da tela.
          </div>
        </div>
      </div>

      {/* ==============================================
          COLUNA DIREITA: MOCKUP DO SISTEMA (Baseado no Print)
          ============================================== */}
      <div className={styles.mockupContainer}>
        <div className={styles.appWindow}>
          
          {/* 1. SIDEBAR ESQUERDA (Fila de Chats) */}
          <div className={styles.wmSidebar}>
            <div className={styles.wmSidebarHeader}>
              <div className={styles.searchBar}><Search size={14} /> Pesquisar...</div>
              <div className={styles.filterTabs}>
                <span className={styles.filterTab}>Meus</span>
                <span className={styles.filterTab}>Fila</span>
                <span className={`${styles.filterTab} ${styles.active}`}>Todos 27</span>
              </div>
            </div>

            <div className={styles.wmChatList}>
              {/* Chat Ativo (Insta) */}
              <div className={`${styles.chatRow} ${styles.active}`}>
                <div className={styles.avatarWrapper}>
                  <img src="https://i.pravatar.cc/150?u=fitness" alt="Loja" className={styles.avatar} />
                  <div className={styles.channelBadge} style={{background: '#E1306C'}}><FaInstagram size={8} /></div>
                </div>
                <div className={styles.rowText}>
                  <span className={styles.rowName}>Loja Fitness Oficial</span>
                  <span className={styles.rowMsg}>Sim, por favor.</span>
                </div>
              </div>

              {/* Chat Secundário (WhatsApp com SLA) */}
              <div className={styles.chatRow}>
                <span className={styles.slaBadge}>SLA Estourado</span>
                <div className={styles.avatarWrapper}>
                  <img src="https://i.pravatar.cc/150?u=diego" alt="Diego" className={styles.avatar} />
                  <div className={styles.channelBadge} style={{background: '#25D366'}}><FaWhatsapp size={8} /></div>
                </div>
                <div className={styles.rowText}>
                  <span className={styles.rowName}>Diego Rezende <span className={styles.unreadCount}>4</span></span>
                  <span className={styles.rowMsg}>
                    <span className={styles.audioMsg}><Mic size={10} /> Áudio</span>
                  </span>
                </div>
              </div>

              <div className={styles.chatRow}>
                <span className={styles.slaBadge}>SLA Estourado</span>
                <div className={styles.avatarWrapper}>
                  <img src="https://i.pravatar.cc/150?u=fabrizio" alt="Fabrizio" className={styles.avatar} />
                  <div className={styles.channelBadge} style={{background: '#25D366'}}><FaWhatsapp size={8} /></div>
                </div>
                <div className={styles.rowText}>
                  <span className={styles.rowName}>Fabrizio <span className={styles.unreadCount} style={{background: '#64748b'}}>6</span></span>
                  <span className={styles.rowMsg}>Ok 👍</span>
                </div>
              </div>
            </div>
          </div>

          {/* 2. CHAT PRINCIPAL (Palco) */}
          <div className={styles.wmMain}>
            <div className={styles.chatHeader}>
              <div className={styles.chatHeaderLeft}>
                <img src="https://i.pravatar.cc/150?u=fitness" alt="" style={{width: 32, height: 32, borderRadius: '50%'}} />
                <div>
                  <div className={styles.chatHeaderName}>Loja Fitness Oficial</div>
                  <div className={styles.chatHeaderVia}>via Comercial Instagram • @lojafitness_oficial</div>
                </div>
              </div>
              <div style={{display: 'flex', gap: '8px'}}>
                <button className={styles.resolveBtn}><Check size={14} /> Resolver</button>
                <div style={{background: '#f1f5f9', padding: '6px', borderRadius: '50%', color: '#64748b'}}><Info size={16}/></div>
              </div>
            </div>

            <div className={styles.chatBody}>
              {step >= 0 && (
                <div className={`${styles.msgBubble} ${styles.msgIn}`}>
                  Vi o stories de vocês! O PDV de vocês funciona offline?
                  <span className={styles.msgTime}>10:40</span>
                </div>
              )}

              {step === 1 && (
                <div className={styles.typingIndicator}>
                  <div className={styles.dot}></div><div className={styles.dot}></div><div className={styles.dot}></div>
                </div>
              )}

              {step >= 2 && (
                <div className={`${styles.msgBubble} ${styles.msgBot}`}>
                  <strong>🤖 Assistente IA:</strong><br/>
                  Olá! Sim, nosso PDV possui contingência offline automática. Deseja falar com um humano para ver uma demonstração?
                  <span className={styles.msgTime}>10:40 <CheckCircle2 size={12} color="#8b5cf6" /></span>
                </div>
              )}

              {step >= 3 && (
                <div className={`${styles.msgBubble} ${styles.msgIn}`}>
                  Sim, por favor.
                  <span className={styles.msgTime}>10:41</span>
                </div>
              )}

              {step >= 4 && (
                <div className={styles.systemAlert}>
                  Atendimento transferido pelo Bot para o setor Comercial.
                </div>
              )}

              {step >= 6 && (
                <div className={styles.msgNote}>
                  <Lock size={12} style={{display: 'inline', marginRight: '4px'}}/> 
                  <strong>Nota Privada (David):</strong> Lead quente veio do Insta. Mandar valores do plano Pro.
                </div>
              )}

              {step >= 8 && (
                <div className={`${styles.msgBubble} ${styles.msgOut}`}>
                  Olá, Loja Fitness! Tudo bem? Vou te mandar a tabela agora.
                  <span className={styles.msgTime}>10:43 <CheckCircle2 size={12} color="#64748b" /></span>
                </div>
              )}
            </div>

            <div className={styles.chatFooter}>
              <div style={{color: '#64748b'}}><Plus size={20} /></div>
              <div className={styles.inputFake}>
                {typedText || <span style={{color: '#94a3b8'}}>Shift + enter para pular linha. Use '/' para atalhos...</span>}
                {(step === 5 || step === 7) && <span className={styles.cursor}></span>}
              </div>
              <div className={`${styles.sendCircle} ${step === 8 ? styles.clicked : ''}`} style={{background: step === 5 ? '#f59e0b' : '#00a884'}}>
                {step === 5 ? <Lock size={16} /> : <Send size={16} />}
              </div>
            </div>
          </div>

          {/* 3. BARRA LATERAL DIREITA (CRM) */}
          <div className={styles.wmDetails}>
            <img src="https://i.pravatar.cc/150?u=fitness" alt="" className={styles.profileAvatar} />
            <div className={styles.profileName}>Loja Fitness Oficial</div>
            
            <button className={styles.crmBtn}>
              <FileText size={14} /> Acessar Painel CRM
            </button>
            <div style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px', fontSize: '0.7rem', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '20px', fontWeight: 700}}>
               <FaInstagram color="#E1306C" /> @lojafitness_oficial
            </div>

            <div className={styles.crmBox}>
              <div className={styles.crmBoxHeader}><Tags size={14} color="#ea580c"/> Tags de Classificação</div>
              <div className={styles.crmTags}>
                {step >= 8 ? (
                  <span className={styles.crmTag}>🔥 Lead Quente</span>
                ) : (
                  <span className={styles.emptyTag}>Nenhuma tag atribuída.</span>
                )}
              </div>
            </div>

            <div className={styles.crmBox}>
              <div className={styles.crmBoxHeader}><KanbanSquare size={14} color="#3b82f6"/> Funil (Pipeline)</div>
              <div className={styles.crmBoxDesc}>Acompanhar negociação.</div>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
}